import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
    Monitor, Smartphone, AlertTriangle, Trash2, LogOut, Loader2, Check,
} from "lucide-react";
import { toast } from "sonner";
import ConfirmModal from "./ConfirmModal";
import PasswordCard from "./PasswordCard";

/**
 * Security section — two main blocks:
 *   1. Active sessions list with per-session revoke + 'revoke all others'.
 *   2. Delete Account flow with typed-email confirmation + 30d grace.
 *
 * If the account is already pending deletion we render a recovery panel
 * instead of the destructive flow.
 */

// ── Helpers ─────────────────────────────────────────────────────────────────
const relativeTime = (iso) => {
    if (!iso) return "—";
    const date = new Date(iso);
    const diff = (Date.now() - date.getTime()) / 1000;
    if (diff < 60) return "щойно";
    if (diff < 3600) return `${Math.floor(diff / 60)} хв тому`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} год тому`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} дн тому`;
    return date.toLocaleDateString("uk-UA");
};

const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("uk-UA", { day: "2-digit", month: "long", year: "numeric" });
};

const daysUntil = (iso) => {
    if (!iso) return 0;
    const diff = (new Date(iso).getTime() - Date.now()) / 86400000;
    return Math.max(0, Math.ceil(diff));
};

const SessionIcon = ({ device }) => {
    const isMobile = /iOS|Android|iPhone|iPad/i.test(device || "");
    return isMobile
        ? <Smartphone size={20} className="text-primary" />
        : <Monitor size={20} className="text-primary" />;
};

// ── Active sessions card ────────────────────────────────────────────────────
const SessionsCard = () => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [revoking, setRevoking] = useState(null);
    const [confirmAllOpen, setConfirmAllOpen] = useState(false);
    const [revokingAll, setRevokingAll] = useState(false);

    const fetchSessions = useCallback(async () => {
        try {
            const { data } = await api.get("/account/sessions");
            setSessions(data.sessions || []);
        } catch (e) {
            console.error("Failed to fetch sessions", e);
            setSessions([]);  // never leave null — defensive
            toast.error("Не вдалось завантажити сесії");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchSessions(); }, [fetchSessions]);

    const handleRevoke = async (sid) => {
        setRevoking(sid);
        try {
            await api.post(`/account/sessions/${sid}/revoke`);
            toast.success("Сесію завершено");
            await fetchSessions();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Не вдалось завершити сесію");
        } finally {
            setRevoking(null);
        }
    };

    const handleRevokeAll = async () => {
        setRevokingAll(true);
        try {
            const { data } = await api.post("/account/sessions/revoke-others");
            toast.success(`Завершено ${data.revoked_count} сесій`);
            setConfirmAllOpen(false);
            await fetchSessions();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Не вдалось завершити сесії");
        } finally {
            setRevokingAll(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-6">
                <Loader2 className="animate-spin text-secondary-c" size={20} />
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="sessions-card">
            <div className="text-sm text-secondary-c mb-3">
                Список пристроїв на яких ви залогінені. Якщо бачите щось підозріле — натисніть «Завершити».
            </div>

            {(sessions || []).length === 0 && (
                <div className="text-center py-4 text-sm text-secondary-c">
                    Активні сесії не знайдено
                </div>
            )}

            {(sessions || []).map((s) => (
                <div
                    key={s.session_id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-surface-hover/40 border border-line-c"
                    data-testid={`session-row-${s.session_id}`}
                >
                    <SessionIcon device={s.device} />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-text-primary text-sm">{s.device}</span>
                            {s.is_current && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary border border-primary/30 flex items-center gap-1">
                                    <Check size={11} /> Це я
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-secondary-c mt-0.5">
                            {s.ip && <span>{s.ip} · </span>}
                            {relativeTime(s.last_seen_at)}
                        </div>
                    </div>
                    {!s.is_current && (
                        <button
                            onClick={() => handleRevoke(s.session_id)}
                            disabled={revoking === s.session_id}
                            className="text-xs px-3 py-1.5 rounded-md border border-line-c hover:border-danger/40 hover:text-danger transition flex items-center gap-1"
                            data-testid={`revoke-session-${s.session_id}`}
                        >
                            {revoking === s.session_id
                                ? <Loader2 size={12} className="animate-spin" />
                                : <LogOut size={12} />}
                            Завершити
                        </button>
                    )}
                </div>
            ))}

            {(sessions || []).length > 1 && (
                <button
                    onClick={() => setConfirmAllOpen(true)}
                    className="w-full mt-2 text-sm px-4 py-2.5 rounded-lg border border-line-c hover:border-danger/40 hover:bg-danger/5 hover:text-danger transition"
                    data-testid="revoke-all-others"
                >
                    Завершити всі інші сесії ({sessions.length - 1})
                </button>
            )}

            <ConfirmModal
                open={confirmAllOpen}
                title="Завершити всі інші сесії?"
                body="Ви будете залогінені тільки на цьому пристрої. На інших пристроях доведеться залогінитись знову."
                confirmLabel="Так, завершити"
                danger
                loading={revokingAll}
                onConfirm={handleRevokeAll}
                onClose={() => setConfirmAllOpen(false)}
                testidPrefix="revoke-all-confirm"
            />
        </div>
    );
};

// ── Delete account card ─────────────────────────────────────────────────────
const DeleteAccountCard = () => {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const [emailInput, setEmailInput] = useState("");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const emailMatches = emailInput.trim().toLowerCase() === (user?.email || "").toLowerCase();

    const handleDelete = async () => {
        setSubmitting(true);
        try {
            await api.post("/account/delete-request", {
                email_confirmation: emailInput.trim(),
                reason: reason.trim() || null,
            });
            toast.success("Запит на видалення прийнято. Перенаправляю...");
            // Wait a tick so toast renders, then logout. Backend already revoked sessions.
            setTimeout(() => {
                window.location.href = "/?deletion=requested";
            }, 1200);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Не вдалось обробити запит");
            setSubmitting(false);
        }
    };

    return (
        <div className="rounded-lg border border-danger/30 bg-danger/5 p-4" data-testid="delete-account-card">
            <div className="flex items-start gap-3">
                <AlertTriangle className="text-danger flex-shrink-0 mt-0.5" size={20} />
                <div className="flex-1">
                    <h4 className="font-semibold text-text-primary">Видалити акаунт</h4>
                    <p className="text-sm text-secondary-c mt-1 leading-relaxed">
                        Ваш акаунт буде помічено на видалення. <b>30 днів</b> можна відмінити —
                        просто увійдіть і натисніть «Відмінити видалення».
                        Через 30 днів усі дані (стратегії, звіти, налаштування) будуть стерті назавжди.
                    </p>
                    <button
                        onClick={() => { setOpen(true); setEmailInput(""); setReason(""); }}
                        className="mt-3 text-sm px-4 py-2 rounded-lg bg-danger/15 text-danger border border-danger/40 hover:bg-danger/25 transition flex items-center gap-2"
                        data-testid="delete-account-trigger"
                    >
                        <Trash2 size={14} />
                        Видалити мій акаунт
                    </button>
                </div>
            </div>

            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-surface rounded-xl border border-danger/30 max-w-md w-full p-6" data-testid="delete-account-modal">
                        <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle className="text-danger" size={20} />
                            <h3 className="font-bold text-lg text-text-primary">Підтвердження видалення</h3>
                        </div>

                        <div className="text-sm text-secondary-c space-y-3 mb-4">
                            <p>Що буде видалено через 30 днів:</p>
                            <ul className="ml-4 list-disc space-y-1 text-xs">
                                <li>Звʼязок з Google Ads акаунтом</li>
                                <li>Усі стратегії, звіти та налаштування</li>
                                <li>Telegram та push-підписки</li>
                                <li>Історія сповіщень</li>
                            </ul>
                            <p className="text-xs">
                                Платіжна інформація буде анонімізована (потрібна для бухобліку згідно з EU-законодавством).
                            </p>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="text-xs text-secondary-c block mb-1">
                                    Введіть свій email для підтвердження:
                                </label>
                                <input
                                    type="email"
                                    value={emailInput}
                                    onChange={(e) => setEmailInput(e.target.value)}
                                    placeholder={user?.email || ""}
                                    autoComplete="off"
                                    className="w-full px-3 py-2 rounded-lg bg-surface-hover border border-line-c focus:border-danger/50 outline-none text-sm"
                                    data-testid="delete-email-input"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-secondary-c block mb-1">
                                    Причина (опціонально):
                                </label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Допоможе нам покращити сервіс..."
                                    rows={2}
                                    maxLength={500}
                                    className="w-full px-3 py-2 rounded-lg bg-surface-hover border border-line-c focus:border-primary/40 outline-none text-sm resize-none"
                                    data-testid="delete-reason-input"
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 mt-5">
                            <button
                                onClick={() => setOpen(false)}
                                disabled={submitting}
                                className="flex-1 px-4 py-2 rounded-lg border border-line-c hover:bg-surface-hover text-sm transition"
                            >
                                Скасувати
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={!emailMatches || submitting}
                                className="flex-1 px-4 py-2 rounded-lg bg-danger text-white font-medium text-sm hover:bg-danger/90 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                data-testid="delete-confirm-button"
                            >
                                {submitting && <Loader2 size={14} className="animate-spin" />}
                                Видалити
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Pending deletion banner (replaces destructive UI when in grace) ─────────
const PendingDeletionCard = ({ status, onCancelled }) => {
    const [cancelling, setCancelling] = useState(false);

    const handleCancel = async () => {
        setCancelling(true);
        try {
            await api.post("/account/cancel-deletion");
            toast.success("Видалення відмінено ✅");
            onCancelled?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Не вдалось відмінити");
            setCancelling(false);
        }
    };

    const days = daysUntil(status.scheduled_for);

    return (
        <div className="rounded-lg border border-warning/40 bg-warning/5 p-5" data-testid="pending-deletion-banner">
            <div className="flex items-start gap-3">
                <AlertTriangle className="text-warning flex-shrink-0 mt-0.5" size={22} />
                <div className="flex-1">
                    <h4 className="font-semibold text-text-primary text-base">
                        Ваш акаунт у процесі видалення
                    </h4>
                    <p className="text-sm text-secondary-c mt-1.5 leading-relaxed">
                        Остаточне видалення: <b className="text-text-primary">{formatDate(status.scheduled_for)}</b>
                        {days > 0 && <span className="text-secondary-c"> · залишилось {days} {days === 1 ? "день" : days < 5 ? "дні" : "днів"}</span>}
                    </p>
                    <p className="text-xs text-secondary-c mt-2">
                        До цього моменту ви можете відмінити видалення і повернути повний доступ.
                    </p>
                    <button
                        onClick={handleCancel}
                        disabled={cancelling}
                        className="mt-4 px-5 py-2.5 rounded-lg bg-primary text-white font-medium text-sm hover:bg-primary/90 transition flex items-center gap-2 disabled:opacity-50"
                        data-testid="cancel-deletion-button"
                    >
                        {cancelling && <Loader2 size={14} className="animate-spin" />}
                        Відмінити видалення
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Main entry ──────────────────────────────────────────────────────────────
const SecuritySection = () => {
    const [deletionStatus, setDeletionStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/account/deletion-status");
            setDeletionStatus(data);
        } catch (e) {
            // Stale token or other error — assume active.
            setDeletionStatus({ status: "active" });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    if (loading) {
        return (
            <div className="flex justify-center py-6">
                <Loader2 className="animate-spin text-secondary-c" size={20} />
            </div>
        );
    }

    const isPending = deletionStatus?.status === "pending";

    return (
        <div className="space-y-6" data-testid="security-section">
            {isPending && (
                <PendingDeletionCard
                    status={deletionStatus}
                    onCancelled={fetchStatus}
                />
            )}

            <div>
                <h3 className="text-sm font-semibold text-text-primary mb-3">Пароль</h3>
                <PasswordCard />
            </div>

            <div>
                <h3 className="text-sm font-semibold text-text-primary mb-3">Активні сесії</h3>
                <SessionsCard />
            </div>

            {!isPending && (
                <div>
                    <h3 className="text-sm font-semibold text-text-primary mb-3">Небезпечна зона</h3>
                    <DeleteAccountCard />
                </div>
            )}
        </div>
    );
};

export default SecuritySection;